import {
  IScheduleBudget, IScheduleBudgetDay, IJobDocument, ISchedulePatternTemplate, ISimpleTime,
  IScheduleShift, IScheduleDailyTimelineData, IScheduleDocument, IBreak, IDate, IClientDocument,
  IResourcePlannerScheduleFilter, IEmployeeTypeData, IScheduleBudgetDayPosition, IScheduleDelay, IScheduleTemplate
} from "../models"
import moment from "moment"
import { IShiftEvent } from "../components/schedule/employee_schedule/monthly"
import AccountStore from "../stores/AccountStore"
import _ from "lodash"
import * as utils from "./utils"
import * as timeHelper from "./timeHelper"
import * as payGroupAndTypeHelper from "./payGroupAndTypeHelper"

export function getOccupationSpecialtyStrings(shiftTemplate) {
  let occupationName = ""
  if (shiftTemplate.RequiredOccupations && shiftTemplate.RequiredOccupations.length > 0) {
    for (let i = 0; i < shiftTemplate.RequiredOccupations.length; i++) {
      occupationName += shiftTemplate.RequiredOccupations[i].Name + (i < shiftTemplate.RequiredOccupations.length - 1 ? ", " : "")
    }
  }


  let specialtyName = ""
  if (shiftTemplate.RequiredSpecialties && shiftTemplate.RequiredSpecialties.length > 0) {
    for (let i = 0; i < shiftTemplate.RequiredSpecialties.length; i++) {
      specialtyName += shiftTemplate.RequiredSpecialties[i].Name + (i < shiftTemplate.RequiredSpecialties.length - 1 ? ", " : "")
    }
  }

  return occupationName + ": " + specialtyName
}

export function getTimelineTableDataFromShift(shiftTemplate, backgroundColor): IScheduleDailyTimelineData {
  let occupationName = ""
  if (shiftTemplate.RequiredOccupations && shiftTemplate.RequiredOccupations.length > 0) {
    for (let i = 0; i < shiftTemplate.RequiredOccupations.length; i++) {
      occupationName += shiftTemplate.RequiredOccupations[i].Name + (i < shiftTemplate.RequiredOccupations.length - 1 ? ", " : "")
    }
  }

  let specialtyName = ""
  if (shiftTemplate.RequiredSpecialties && shiftTemplate.RequiredSpecialties.length > 0) {
    for (let i = 0; i < shiftTemplate.RequiredSpecialties.length; i++) {
      specialtyName += shiftTemplate.RequiredSpecialties[i].Name + (i < shiftTemplate.RequiredSpecialties.length - 1 ? ", " : "")
    }
  }

  return {
    StartTime: shiftTemplate.StartTime,
    EndTime: shiftTemplate.EndTime,
    Used: shiftTemplate.Used,
    PunchedIn: shiftTemplate.PunchInTime ? true : false,
    PunchedOut: shiftTemplate.PunchOutTime ? true : false,
    Breaks: shiftTemplate.Breaks,
    Color: backgroundColor,
    OccupationName: occupationName,
    SpecialtyName: specialtyName,
    PositionName: shiftTemplate.Position ? shiftTemplate.Position.Name : ""
  } as IScheduleDailyTimelineData
}

export function getPatternTemplates(schedules: IScheduleDocument[]): ISchedulePatternTemplate[] {
  let patternTemplates = []
  schedules.forEach(schedule => {
    schedule.Schedules.forEach(patternTemplate => {
      if (!patternTemplate.Deleted) {
        patternTemplates.push(patternTemplate)
      }
    })
  })

  return patternTemplates
}

export function getFilledPercentArrayForDaysByScheduleList(startDate: IDate, days: number[], schedules: IScheduleDocument[]) {
  // startDate is local time
  const patternTemplates = getPatternTemplates(schedules)
  return getFilledPercentArrayForDays(startDate, days, patternTemplates)
}

export function getFilledPercentArrayForDays(startDate: IDate, days: number[], patternTemplates: ISchedulePatternTemplate[]) {
  // startDate is local time
  const filledPercentArrayForDays = {}
  days.forEach(day => {
    const dayPatternTemplates = getPatternTemplatesForDay(moment(startDate).utc(true).format(), day, patternTemplates)
    let totalShiftsCount = 0
    let totalFilledShiftsCount = 0
    dayPatternTemplates.forEach(pt => {
      totalShiftsCount++
      pt.AssignedUserId && totalFilledShiftsCount++
    })
    filledPercentArrayForDays[day] = totalShiftsCount === 0 ? Number.NaN : Math.round(totalFilledShiftsCount * 100 / totalShiftsCount)
  })
  return filledPercentArrayForDays
}


export const getEventTimeRange = (_event: IShiftEvent): string => `${timeHelper.getShortTime(_event.start)}-${timeHelper.getShortTime(_event.end)}`

export function getTimeFromSimpleTime(simpleTime: ISimpleTime) {
  return moment().year(simpleTime.Year).month(simpleTime.Month).date(simpleTime.Day).hour(simpleTime.Hour).minute(simpleTime.Minute).second(0).millisecond(0)
}

export function getSimpleTimeFromTime(time): ISimpleTime {
  return {
    Year: moment(time).year(),
    Month: moment(time).month(),
    Day: moment(time).date(),
    Hour: moment(time).hour(),
    Minute: moment(time).minute(),
  }
}

export function getDurationMiutesFromShiftSimpleTimes(simpleStartTime: ISimpleTime, simpleEndTime: ISimpleTime) {
  const startTime = getTimeFromSimpleTime(simpleStartTime)
  const endTime = getTimeFromSimpleTime(simpleEndTime)
  return endTime.diff(startTime, "minute")
}

export function breakWindowTimesIsFallInShiftTimes(simpleShiftStartTime: ISimpleTime, simpleShiftEndTime: ISimpleTime, simpleBreakWindowStartTime: ISimpleTime, simpleBreakWindowEndTime: ISimpleTime): boolean {
  const shiftStartTime = getTimeFromSimpleTime(simpleShiftStartTime)
  const shiftEndTime = getTimeFromSimpleTime(simpleShiftEndTime)
  const breakStartTime = getTimeFromSimpleTime(simpleBreakWindowStartTime)
  const breakEndTime = getTimeFromSimpleTime(simpleBreakWindowEndTime)
  return (
    moment(breakStartTime).isSameOrAfter(shiftStartTime) &&
    moment(breakStartTime).isSameOrBefore(shiftEndTime) &&
    moment(breakEndTime).isSameOrAfter(shiftStartTime) &&
    moment(breakEndTime).isSameOrBefore(shiftEndTime)
  )
}

export function checkValidateShiftBreakTimes(shift: IScheduleShift): boolean {
  return !shift.Breaks || shift.Breaks.length === 0 ?
    true :
    !shift.Breaks.find(item => !item.Deleted && !breakWindowTimesIsFallInShiftTimes(shift.StartTime, shift.EndTime, item.BreakWindowStartTime, item.BreakWindowEndTime))
}

export function getVirtualShiftTime(startTime: ISimpleTime, endTime: ISimpleTime) {
  const virtualStartTime = _.cloneDeep(AccountStore.ScheduleVirtualSimpleTime)
  virtualStartTime.Hour = startTime.Hour
  virtualStartTime.Minute = startTime.Minute
  const virtualEndTime = _.cloneDeep(AccountStore.ScheduleVirtualSimpleTime)
  virtualEndTime.Hour = endTime.Hour
  virtualEndTime.Minute = endTime.Minute
  if (getTimeFromSimpleTime(virtualStartTime).isAfter(getTimeFromSimpleTime(virtualEndTime))) {
    virtualEndTime.Day = 2
  }
  return { VirtualStartTime: virtualStartTime, VirtualEndTime: virtualEndTime }
}

export function getVirtualBreakTime(restrictedStartTime: ISimpleTime, restrictedEndTime: ISimpleTime, breakStartTime: ISimpleTime, breakEndTime: ISimpleTime) {
  const virtualBreakTime = getVirtualShiftTime(breakStartTime, breakEndTime)
  const virtualBreakStartTime = virtualBreakTime.VirtualStartTime
  const virtualBreakEndTime = virtualBreakTime.VirtualEndTime
  if (restrictedStartTime && restrictedEndTime && !breakWindowTimesIsFallInShiftTimes(restrictedStartTime, restrictedEndTime, virtualBreakStartTime, virtualBreakEndTime)) {
    virtualBreakStartTime.Day++
    virtualBreakEndTime.Day++
  }
  return { VitualBreakStartTime: virtualBreakStartTime, VirtualBreakEndTime: virtualBreakEndTime }
}

export function checkOverlapShifts1(shifts: IScheduleShift[]) {
  if (shifts.length < 2) return true
  const lastItem = shifts[0]
  const previousItems = shifts.slice().splice(-1, 1)
  return checkOverlapShifts(lastItem, previousItems)
}

export function checkOverlapShifts(newShift: IScheduleShift, shifts: IScheduleShift[]) {
  let isNoOverlap = true
  shifts.forEach(item => {
    if (getTimeFromSimpleTime(newShift.StartTime).isBefore(getTimeFromSimpleTime(item.EndTime)) && getTimeFromSimpleTime(item.StartTime).isBefore(getTimeFromSimpleTime(newShift.EndTime))) {
      isNoOverlap = false
    }
    if (getTimeFromSimpleTime(item.StartTime).isBefore(getTimeFromSimpleTime(newShift.EndTime)) && getTimeFromSimpleTime(newShift.StartTime).isBefore(getTimeFromSimpleTime(item.EndTime))) {
      isNoOverlap = false
    }
    if (getTimeFromSimpleTime(newShift.StartTime).isBefore(getTimeFromSimpleTime(item.StartTime)) && getTimeFromSimpleTime(item.EndTime).isBefore(getTimeFromSimpleTime(newShift.EndTime))) {
      isNoOverlap = false
    }
    if (getTimeFromSimpleTime(item.StartTime).isBefore(getTimeFromSimpleTime(newShift.StartTime)) && getTimeFromSimpleTime(newShift.EndTime).isBefore(getTimeFromSimpleTime(item.EndTime))) {
      isNoOverlap = false
    }
  })
  return isNoOverlap
}

export function getPositionSimpleTimeInTimeline(coordinateTime: ISimpleTime, widthPerHour: number, simpleTime: ISimpleTime) {
  const diffMinutes = getDurationMiutesFromShiftSimpleTimes(coordinateTime, simpleTime)
  const positionX = Math.round((diffMinutes / 60) * widthPerHour)
  return positionX
}

export function getSimpleTimeFromPositionX(coordinate: ISimpleTime, widthPerHour: number, positionX: number): ISimpleTime {
  const minuteIncreasement = 15
  let day = Math.floor((Math.floor(positionX / widthPerHour)) / 24)
  let hour = Math.floor(positionX / widthPerHour) % 24
  let minute = Math.round((positionX % widthPerHour) * (60 / minuteIncreasement) / widthPerHour) * minuteIncreasement
  if (minute === 60) {
    hour += 1
    minute = 0
  }
  if (hour === 24) {
    day += 1
    hour = 0
  }
  const time = getTimeFromSimpleTime(coordinate)
  time.add(day, "day")
  return {
    Year: time.year(),
    Month: time.month(),
    Day: time.date(),
    Hour: hour,
    Minute: minute
  }
}

export function getScheduleTimelineRightSideWidthInfo(timelineBarHourLength, timelineTableWidth, leftSideWidth) {
  const widthPerHour = Math.floor((timelineTableWidth - leftSideWidth) / timelineBarHourLength)
  const rightSideWidth = widthPerHour * timelineBarHourLength
  return { widthPerHour, rightSideWidth }
}

export function getCoordinateTime(timelineData: IScheduleDailyTimelineData[][]): ISimpleTime {
  const allTimeData: IScheduleDailyTimelineData[] = []
  timelineData.forEach(row =>
    row.forEach(item =>
      allTimeData.push(item)
    )
  )

  if (!allTimeData.length) return AccountStore.ScheduleVirtualSimpleTime

  let ealiestTime
  allTimeData.forEach(item => {
    const time = getTimeFromSimpleTime(item.StartTime)
    if (!ealiestTime || ealiestTime.isAfter(time)) {
      ealiestTime = time
    }
  })
  return {
    Year: ealiestTime.year(),
    Month: ealiestTime.month(),
    Day: ealiestTime.date(),
    Hour: 0,
    Minute: 0
  }
}

export function getTimelineBarHourLength(timelineData: IScheduleDailyTimelineData[][]): number {
  let latestTime
  timelineData.forEach(row =>
    row.forEach(item => {
      const time = getTimeFromSimpleTime(item.EndTime)
      if (!latestTime || latestTime.isBefore(time)) {
        latestTime = time
      }
    })
  )
  if (!latestTime) return 24
  const ealiestTime = getTimeFromSimpleTime(getCoordinateTime(timelineData))
  const diff = latestTime.diff(ealiestTime, "hour")
  return diff < 24 ? 24 : Math.floor(diff + 1)
}

export function getStartEndSimpleTimeOfPatternTemplate(patternTemplate: ISchedulePatternTemplate) {
  let ptEaliestSimpleTime: ISimpleTime
  let ptLatestSimpleTime: ISimpleTime
  patternTemplate.ShiftTemplates.forEach(st => {
    if (!ptEaliestSimpleTime || getTimeFromSimpleTime(ptEaliestSimpleTime).isAfter(getTimeFromSimpleTime(st.StartTime))) {
      ptEaliestSimpleTime = st.StartTime
    }
    if (!ptLatestSimpleTime || getTimeFromSimpleTime(ptLatestSimpleTime).isBefore(getTimeFromSimpleTime(st.EndTime))) {
      ptLatestSimpleTime = st.EndTime
    }
  })
  return { StartSimpleTime: ptEaliestSimpleTime, EndSimpleTime: ptLatestSimpleTime }
}

export function getStartEndSimpleTimeFromPatternTemplates(patternTemplates: ISchedulePatternTemplate[]) {
  const shiftTimes = []
  patternTemplates.forEach(pt => {
    let ptEaliestSimpleTime: ISimpleTime
    let ptLatestSimpleTime: ISimpleTime
    pt.ShiftTemplates.forEach(st => {
      if (!ptEaliestSimpleTime || getTimeFromSimpleTime(ptEaliestSimpleTime).isAfter(getTimeFromSimpleTime(st.StartTime))) {
        ptEaliestSimpleTime = st.StartTime
      }
      if (!ptLatestSimpleTime || getTimeFromSimpleTime(ptLatestSimpleTime).isBefore(getTimeFromSimpleTime(st.EndTime))) {
        ptLatestSimpleTime = st.EndTime
      }
    })
    const diffDays = getTimeFromSimpleTime(ptEaliestSimpleTime).diff(getTimeFromSimpleTime(AccountStore.ScheduleVirtualSimpleTime), "day")
    const startTime = getTimeFromSimpleTime(ptEaliestSimpleTime).subtract(diffDays, "day")
    const endTime = getTimeFromSimpleTime(ptLatestSimpleTime).subtract(diffDays, "day")
    shiftTimes.push({ StartTime: startTime, EndTime: endTime })
  })

  let ealiestTime
  let latestTime
  shiftTimes.forEach(item => {
    if (!ealiestTime || ealiestTime.isAfter(item.StartTime)) {
      ealiestTime = item.StartTime
    }
    if (!latestTime || latestTime.isBefore(item.EndTime)) {
      latestTime = item.EndTime
    }
  })
  return { Start: getSimpleTimeFromTime(ealiestTime), End: getSimpleTimeFromTime(latestTime) }
}

export function durationInDays(schedules: IScheduleDocument[]): number {
  var duration = moment.duration(moment(getEndDate(schedules)).diff(getStartDate(schedules)))
  var returnVal = Math.floor(duration.asDays() + 1)
  if (returnVal > 60) {
    return 60
  } else {
    return returnVal
  }
}

export function getStartDate(schedules: IScheduleDocument[]): IDate {
  const patternTemplates = getPatternTemplates(schedules).sort((a, b) => {
    const aStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(a).StartSimpleTime)
    const bStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(b).StartSimpleTime)
    if (aStartTime.isBefore(bStartTime)) return -1
    if (aStartTime.isAfter(bStartTime)) return 1
    return 0
  })
  if (patternTemplates && patternTemplates.length > 0) {
    return getTimeFromSimpleTime(patternTemplates[0].ShiftTemplates[0].StartTime).startOf("day")
  }
  else {
    const sortedSchedules = schedules.sort((a, b) => {
      const aStartTime = moment(a.StartDate)
      const bStartTime = moment(b.StartDate)
      if (aStartTime.isBefore(bStartTime)) return -1
      if (aStartTime.isAfter(bStartTime)) return 1
      return 0
    })
    return moment(timeHelper.keepConvertUTCToLocal(sortedSchedules[0].StartDate)).startOf("day")
  }
}

export function getEndDate(schedules: IScheduleDocument[]): IDate {
  const patternTemplates = getPatternTemplates(schedules).sort((a, b) => {
    const aStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(a).StartSimpleTime)
    const bStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(b).StartSimpleTime)
    if (aStartTime.isBefore(bStartTime)) return -1
    if (aStartTime.isAfter(bStartTime)) return 1
    return 0
  })
  if (patternTemplates && patternTemplates.length > 0) {
    return getTimeFromSimpleTime(patternTemplates[patternTemplates.length - 1].ShiftTemplates[0].StartTime).endOf("day")
  }
  else {
    const sortedSchedules = schedules.sort((a, b) => {
      const aEndTime = moment(a.EndDate)
      const bEndTime = moment(b.EndDate)
      if (aEndTime.isBefore(bEndTime)) return -1
      if (aEndTime.isAfter(bEndTime)) return 1
      return 0
    })
    return moment(timeHelper.keepConvertUTCToLocal(sortedSchedules[0].EndDate)).endOf("day")
  }
}

export function getPatternTemplatesForDay(startTime: IDate, day: number, patternTemplates: ISchedulePatternTemplate[]): ISchedulePatternTemplate[] {
  //it's local time of UTC format
  const startTimeOfDay = moment(timeHelper.keepConvertUTCToLocal(startTime)).startOf("day").add(day, "day")
  return patternTemplates.filter(pt =>
    pt.ShiftTemplates.find(st => getTimeFromSimpleTime(st.StartTime).startOf("day").isSame(startTimeOfDay))
  )
}

export function getDelaysForDay(startTime: IDate, day: number, delays: IScheduleDelay[]): IScheduleDelay[] {
  //it's local time of UTC format
  if (!delays) return delays
  const startTimeOfDay = moment(timeHelper.keepConvertUTCToLocal(startTime)).startOf("day").add(day, "day")
  return delays.filter(i => moment(timeHelper.keepConvertUTCToLocal(i.DateTimeLocal)).startOf("day").isSame(startTimeOfDay))
}

export function getDailyBudgetByDate(dayDate: IDate, days: IScheduleBudgetDay[]): IScheduleBudgetDay {
  // dayDate is local time
  const startTimeOfDay = moment(dayDate).startOf("day")
  const returnVal = days.filter(day =>
    moment(timeHelper.keepConvertUTCToLocal(day.Date)).startOf("day").isSame(startTimeOfDay)
  )

  return returnVal[0]
}

export function getPatternTemplatesByDate(day: IDate, patternTemplates: ISchedulePatternTemplate[]): ISchedulePatternTemplate[] {
  // day is local time
  const startTimeOfDay = moment(day).startOf("day")
  const returnVal = patternTemplates.filter(pt =>
    getTimeFromSimpleTime(pt.ShiftTemplates[0].StartTime).startOf("day").isSame(startTimeOfDay)
  )
  return returnVal
}

export function getTransparentBackgroundColorFade(color, fadeColor) {
  return "linear-gradient(90deg, " + getTransparentBackgroundColor(color) + "," + fadeColor + ")"
}

export function getTransparentBackgroundColor(color) {
  return color + "11"
}

export function getDayLengthFromStartAndEndTime(startTime: IDate, endTime: IDate): number {
  //startTime and endTime is LocalTime UTC format
  return moment.utc(endTime).diff(moment.utc(startTime), "day") + 1
}

export function getDayLengthFromPatternTemplates(startTime: IDate, patternTemplates: ISchedulePatternTemplate[]): number {
  let latestStartTime = moment(startTime).utc()
  patternTemplates.forEach(pt =>
    pt.ShiftTemplates.forEach(st => {
      if (latestStartTime.isBefore(getTimeFromSimpleTime(st.StartTime).utc(true))) {
        latestStartTime = getTimeFromSimpleTime(st.StartTime).utc(true)
      }
    })
  )
  return latestStartTime.diff(moment(startTime).utc(), "day") + 1
}

export function updatePatternTemplateTimeForDay(startTime: IDate, day: number, patternTemplate: ISchedulePatternTemplate): ISchedulePatternTemplate {
  // startTime is Local time
  const startTimeOfDay = moment(startTime).add(day, "day")
  const updatedPatternTemplate = _.cloneDeep(patternTemplate)
  updatedPatternTemplate.ShiftTemplates.forEach(st => {
    const diffDays = startTimeOfDay.diff(moment(getTimeFromSimpleTime(st.StartTime)).startOf("day"), "day")
    st.StartTime = getSimpleTimeFromTime(moment(getTimeFromSimpleTime(st.StartTime)).add(diffDays, "day"))
    st.EndTime = getSimpleTimeFromTime(moment(getTimeFromSimpleTime(st.EndTime)).add(diffDays, "day"))
    st.Breaks.forEach(breakItem => {
      breakItem.BreakWindowStartTime = getSimpleTimeFromTime(moment(getTimeFromSimpleTime(breakItem.BreakWindowStartTime)).add(diffDays, "day"))
      breakItem.BreakWindowEndTime = getSimpleTimeFromTime(moment(getTimeFromSimpleTime(breakItem.BreakWindowEndTime)).add(diffDays, "day"))
    })
  })
  return updatedPatternTemplate
}

export function udpateDailyBudgets(startDate: IDate, budget: IScheduleBudget): IScheduleBudget {
  let modifiedBudget = _.cloneDeep(budget)
  if (modifiedBudget && modifiedBudget.Days.length) {
    //Use the first staff plan item (daily budget item) to get the diff
    const diffDays = moment(timeHelper.keepConvertUTCToLocal(startDate)).diff(moment(timeHelper.keepConvertUTCToLocal(budget.Days[0].Date)), "day")

    modifiedBudget.Days.forEach(item => {
      item.Date = moment(timeHelper.keepConvertUTCToLocal(item.Date)).add(diffDays, "day").utc().toDate()
    })
  }
  return modifiedBudget
}

export function updateVirtualTimePatternTemplates(startDate: IDate, virtualStartDate: IDate, patternTemplates: ISchedulePatternTemplate[]): ISchedulePatternTemplate[] {
  // startDate && virtualStartDate are local time utc format
  const diffDays = moment(timeHelper.keepConvertUTCToLocal(startDate)).diff(moment(timeHelper.keepConvertUTCToLocal(virtualStartDate)), "day")
  patternTemplates.forEach(pt => {
    pt.Id = utils.guid()
    pt.ShiftTemplates.forEach(st => {
      st.Id = utils.guid()
      st.StartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(st.StartTime).add(diffDays, "day"))
      st.EndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(st.EndTime).add(diffDays, "day"))
      st.Breaks.forEach(breakItem => {
        breakItem.BreakWindowStartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowStartTime).add(diffDays, "day"))
        breakItem.BreakWindowEndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowEndTime).add(diffDays, "day"))
      })
    })
  })
  return patternTemplates
}

export function getVirtualShiftTemplateFromRealShiftTemplate(realShiftTemplate: IScheduleShift): IScheduleShift {
  let virtualShiftTemplate = _.cloneDeep(realShiftTemplate)
  const diffDaysBasedVirtualStartDate = getTimeFromSimpleTime(realShiftTemplate.StartTime).diff(getTimeFromSimpleTime(AccountStore.ScheduleVirtualSimpleTime), "day")
  virtualShiftTemplate.StartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(virtualShiftTemplate.StartTime).subtract(diffDaysBasedVirtualStartDate, "day"))
  virtualShiftTemplate.EndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(virtualShiftTemplate.EndTime).subtract(diffDaysBasedVirtualStartDate, "day"))
  virtualShiftTemplate.Breaks.forEach(breakItem => {
    breakItem.BreakWindowStartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowStartTime).subtract(diffDaysBasedVirtualStartDate, "day"))
    breakItem.BreakWindowEndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowEndTime).subtract(diffDaysBasedVirtualStartDate, "day"))
  })
  return virtualShiftTemplate
}

export function getRealShiftTemplateFromVirtualShiftTemplate(realShiftStartDateTime: IDate, virtualShiftTemplate: IScheduleShift): IScheduleShift {
  let realShiftTemplate = _.cloneDeep(virtualShiftTemplate)
  const diffDaysBasedRealStartDate = moment(realShiftStartDateTime).diff(getTimeFromSimpleTime(AccountStore.ScheduleVirtualSimpleTime), "day")
  realShiftTemplate.StartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(realShiftTemplate.StartTime).add(diffDaysBasedRealStartDate, "day"))
  realShiftTemplate.EndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(realShiftTemplate.EndTime).add(diffDaysBasedRealStartDate, "day"))
  realShiftTemplate.Breaks.forEach(breakItem => {
    breakItem.BreakWindowStartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowStartTime).add(diffDaysBasedRealStartDate, "day"))
    breakItem.BreakWindowEndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowEndTime).add(diffDaysBasedRealStartDate, "day"))
  })
  return realShiftTemplate
}

export function getVirtualPatternTemplateFromRealPatternTemplate(realPatternTemplate: ISchedulePatternTemplate): ISchedulePatternTemplate {
  let virtualPatternTemplate = _.cloneDeep(realPatternTemplate)
  const realStartDateOfPatternTemplate = getStartEndSimpleTimeOfPatternTemplate(realPatternTemplate).StartSimpleTime
  const diffDaysBasedVirtualStartDate = getTimeFromSimpleTime(realStartDateOfPatternTemplate).diff(getTimeFromSimpleTime(AccountStore.ScheduleVirtualSimpleTime), "day")
  virtualPatternTemplate.ShiftTemplates.forEach(st => {
    st.StartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(st.StartTime).subtract(diffDaysBasedVirtualStartDate, "day"))
    st.EndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(st.EndTime).subtract(diffDaysBasedVirtualStartDate, "day"))
    st.Breaks.forEach(breakItem => {
      breakItem.BreakWindowStartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowStartTime).subtract(diffDaysBasedVirtualStartDate, "day"))
      breakItem.BreakWindowEndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(breakItem.BreakWindowEndTime).subtract(diffDaysBasedVirtualStartDate, "day"))
    })
  })
  return virtualPatternTemplate
}

export function overridePatternTemplatesHierarchy(patternTemplates: ISchedulePatternTemplate[], hierarchy: string, scheduleJob: IJobDocument, scheduleClient: IClientDocument): ISchedulePatternTemplate[] {
  patternTemplates.forEach(pt => {
    overridePatternTemplateHierarchy(pt, hierarchy, scheduleJob, scheduleClient)
  })
  return patternTemplates
}

export function overridePatternTemplateHierarchy(patternTemplate: ISchedulePatternTemplate, hierarchy: string, scheduleJob: IJobDocument, scheduleClient: IClientDocument): ISchedulePatternTemplate {
  patternTemplate.HierarchyId = hierarchy

  patternTemplate.ShiftTemplates.forEach(st => {
    st.HierarchyId = hierarchy
    st.JobId = scheduleJob ? scheduleJob.Id : null
    st.JobName = scheduleJob ? scheduleJob.Name : null
    st.PhaseId = null
    st.PhaseName = null
    st.ClientId = scheduleClient ? scheduleClient.Id : null
    st.ClientName = scheduleClient ? scheduleClient.Name : null
  })
  return patternTemplate
}

export function overrideShiftTemplateHierarchy(shiftTemplate: IScheduleShift, hierarchy: string, scheduleJob: IJobDocument, scheduleClient: IClientDocument): IScheduleShift {
  shiftTemplate.HierarchyId = hierarchy
  shiftTemplate.JobId = scheduleJob ? scheduleJob.Id : null
  shiftTemplate.JobName = scheduleJob ? scheduleJob.Name : null
  shiftTemplate.PhaseId = null
  shiftTemplate.PhaseName = null
  shiftTemplate.ClientId = scheduleClient ? scheduleClient.Id : null
  shiftTemplate.ClientName = scheduleClient ? scheduleClient.Name : null
  return shiftTemplate
}

// include originPT
// one pt per one day
export function getCopiedUnassignedPTs(_schedule: IScheduleDocument, _originPT: ISchedulePatternTemplate): ISchedulePatternTemplate[] {
  const schedule = _.cloneDeep(_schedule)
  const originPT = _.cloneDeep(_originPT)
  const copiedUnassignedPTs: ISchedulePatternTemplate[] = [originPT]
  schedule.Schedules.filter(i => !i.Deleted).forEach(pt => {
    if (pt.Id === originPT.Id || pt.AssignedUserId) return
    const ptStartTime = getStartEndSimpleTimeOfPatternTemplate(pt).StartSimpleTime
    const exist = copiedUnassignedPTs.find(existPt => {
      const existPtStartTime = getStartEndSimpleTimeOfPatternTemplate(existPt).StartSimpleTime
      return ptStartTime.Year === existPtStartTime.Year &&
        ptStartTime.Month === existPtStartTime.Month &&
        ptStartTime.Day === existPtStartTime.Day
    })
    !exist && checkCopiedPatternTemplates(pt, originPT) &&
      copiedUnassignedPTs.push(pt)
  })

  // sort by date
  copiedUnassignedPTs.sort((a, b) => {
    const aStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(a).StartSimpleTime)
    const bStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(b).StartSimpleTime)
    if (aStartTime.isBefore(bStartTime)) return -1
    if (aStartTime.isAfter(bStartTime)) return 1
    return 0
  })
  return copiedUnassignedPTs
}

export function getCopiedAssignedPTs(_schedule: IScheduleDocument, _originPT: ISchedulePatternTemplate): ISchedulePatternTemplate[] {
  const schedule = _.cloneDeep(_schedule)
  const originPT = _.cloneDeep(_originPT)
  const copiedAssignedPTs: ISchedulePatternTemplate[] = [originPT]
  schedule.Schedules.filter(i => !i.Deleted).forEach(pt => {
    if (pt.Id === originPT.Id || pt.AssignedUserId !== originPT.AssignedUserId) return
    const ptStartTime = getStartEndSimpleTimeOfPatternTemplate(pt).StartSimpleTime
    const exist = copiedAssignedPTs.find(existPt => {
      const existPtStartTime = getStartEndSimpleTimeOfPatternTemplate(existPt).StartSimpleTime
      return ptStartTime.Year === existPtStartTime.Year &&
        ptStartTime.Month === existPtStartTime.Month &&
        ptStartTime.Day === existPtStartTime.Day
    })
    !exist && checkCopiedPatternTemplates(pt, originPT) &&
      copiedAssignedPTs.push(pt)
  })

  // sort by date
  copiedAssignedPTs.sort((a, b) => {
    const aStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(a).StartSimpleTime)
    const bStartTime = getTimeFromSimpleTime(getStartEndSimpleTimeOfPatternTemplate(b).StartSimpleTime)
    if (aStartTime.isBefore(bStartTime)) return -1
    if (aStartTime.isAfter(bStartTime)) return 1
    return 0
  })
  return copiedAssignedPTs
}

function checkCopiedPatternTemplates(pt1: ISchedulePatternTemplate, pt2: ISchedulePatternTemplate) {
  if (pt1.Name !== pt2.Name) return false
  if (pt1.Description !== pt2.Description) return false
  if (pt1.HierarchyId !== pt2.HierarchyId) return false
  if (pt1.Deleted !== pt2.Deleted) return false

  const sts1 = pt1.ShiftTemplates.filter(item => !item.Deleted)
  const sts2 = pt2.ShiftTemplates.filter(item => !item.Deleted)
  if (sts1.length !== sts2.length) return false
  let sameSt = true
  sts1.forEach(st1 => {
    if (!sts2.find(st2 => checkCopiedShiftTemplates(st1, st2)))
      sameSt = false
  })
  if (!sameSt) return false
  return true
}

function checkCopiedShiftTemplates(st1: IScheduleShift, st2: IScheduleShift): boolean {
  if (st1.Name !== st2.Name) return false
  if (st1.ExternalId !== st2.ExternalId) return false
  if (st1.HierarchyId !== st2.HierarchyId) return false
  if (st1.Private != st2.Private) return false
  if (st1.Description !== st2.Description) return false
  if (st1.PositionId !== st2.PositionId) return false
  if (st1.StartTime.Hour !== st2.StartTime.Hour) return false
  if (st1.StartTime.Minute !== st2.StartTime.Minute) return false
  if (st1.EndTime.Hour !== st2.EndTime.Hour) return false
  if (st1.EndTime.Minute !== st2.EndTime.Minute) return false

  const breaks1 = st1.Breaks.filter(item => !item.Deleted)
  const breaks2 = st2.Breaks.filter(item => !item.Deleted)
  if (breaks1.length !== breaks2.length) return false
  let sameBreak = true
  breaks1.forEach(bk1 => {
    if (!breaks2.find(bk2 => checkCopiedBreaks(bk1, bk2)))
      sameBreak = false
  })
  if (!sameBreak) return false

  if (st1.EarlyPunchRestriction !== st2.EarlyPunchRestriction) return false
  if (st1.EarlyPunchRestrictionMinutes !== st2.EarlyPunchRestrictionMinutes) return false
  if (st1.DifferentialPayFactor !== st2.DifferentialPayFactor) return false
  if (st1.Deleted !== st2.Deleted) return false
  if (st1.PayTypeId !== st2.PayTypeId) return false
  if (st1.Used !== st2.Used) return false
  if (st1.JobId !== st2.JobId) return false
  if (st1.JobName !== st2.JobName) return false
  if (st1.PhaseId !== st2.PhaseId) return false
  if (st1.PhaseName !== st2.PhaseName) return false
  return true
}

function checkCopiedBreaks(break1: IBreak, break2: IBreak): boolean {
  if (break1.Name !== break2.Name) return false
  if (break1.BreakWindowStartTime.Hour !== break2.BreakWindowStartTime.Hour) return false
  if (break1.BreakWindowStartTime.Minute !== break2.BreakWindowStartTime.Minute) return false
  if (break1.BreakWindowEndTime.Hour !== break2.BreakWindowEndTime.Hour) return false
  if (break1.BreakWindowEndTime.Minute !== break2.BreakWindowEndTime.Minute) return false
  if (break1.MinimumBreakDuration !== break2.MinimumBreakDuration) return false
  if (break1.MaximumBreakDuration !== break2.MaximumBreakDuration) return false
  if (break1.RecommendedBreakDuration !== break2.RecommendedBreakDuration) return false
  if (break1.RequirePunchBiometrics !== break2.RequirePunchBiometrics) return false
  if (break1.PayTypeId !== break2.PayTypeId) return false
  if (break1.Deleted !== break2.Deleted) return false
  return true
}

export function initScheduleSchema(startDate: IDate, schedule: (IScheduleDocument | IScheduleTemplate)) {
  const isScheduleTemplate = !schedule.hasOwnProperty("AccountId")
  var diffDays = moment(startDate).diff(timeHelper.keepConvertUTCToLocal(schedule.StartDate), "days")
  schedule.Id = utils.guid()
  schedule.StartDate = moment(startDate).utc(true).format()
  schedule.EndDate = moment(timeHelper.keepConvertUTCToLocal(schedule.EndDate)).add(diffDays, "days").utc(true).format()
  if (!isScheduleTemplate) {
    (schedule as IScheduleDocument).PublishInfo = null
  }

  const schedules = isScheduleTemplate ?
    (schedule as IScheduleTemplate).PatternTemplates :
    (schedule as IScheduleDocument).Schedules

  schedules.forEach(pt => {
    pt.Id = utils.guid()
    pt.ShiftTemplates = pt.ShiftTemplates.map(shift => {
      shift.Id = utils.guid()
      shift.StartDateTime = moment(timeHelper.keepConvertUTCToLocal(shift.StartDateTime)).add(diffDays, "days").utc(true).format()
      shift.EndDateTime = moment(timeHelper.keepConvertUTCToLocal(shift.EndDateTime)).add(diffDays, "days").utc(true).format()
      shift.StartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(shift.StartTime).add(diffDays, "days"))
      shift.EndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(shift.EndTime).add(diffDays, "days"))

      shift.Breaks &&
        shift.Breaks.forEach(i => {
          i.BreakWindowStartDateTime = moment(timeHelper.keepConvertUTCToLocal(i.BreakWindowStartDateTime)).add(diffDays, "days").utc(true).format()
          i.BreakWindowEndDateTime = moment(timeHelper.keepConvertUTCToLocal(i.BreakWindowEndDateTime)).add(diffDays, "days").utc(true).format()
          i.BreakWindowStartTime = getSimpleTimeFromTime(getTimeFromSimpleTime(i.BreakWindowStartTime).add(diffDays, "days"))
          i.BreakWindowEndTime = getSimpleTimeFromTime(getTimeFromSimpleTime(i.BreakWindowEndTime).add(diffDays, "days"))
        })
      shift.Used = false
      shift.PunchInTime = null
      shift.PunchOutTime = null

      return shift
    })
  })

  if (isScheduleTemplate) {
    (schedule as IScheduleTemplate).PatternTemplates = schedules
  }
  else {
    (schedule as IScheduleDocument).Schedules = schedules
  }
  return schedule
}

export function isNonProductive(pt: ISchedulePatternTemplate) {
  if (pt.ShiftTemplates && pt.ShiftTemplates[0].Position && pt.ShiftTemplates[0].Position.DefaultPayClass) {
    return payGroupAndTypeHelper.getPayTypeById(pt.ShiftTemplates[0].Position.DefaultPayClass.PayTypeId).NonProductiveTime
  }
  return false
}

export function checkValidJobShiftDayByJobDate(job: IJobDocument, selectedDayLocal: IDate): boolean {
  if (job && job.HasStartEndDates) {
    const selectedDayMoment = moment(selectedDayLocal)
    return selectedDayMoment.isSameOrAfter(job.StartDateLocal) && selectedDayMoment.isSameOrBefore(job.EndDateLocal)
  }
  else {
    return true
  }
}

export function getMarginColor(patternTemplate: ISchedulePatternTemplate) {
  return patternTemplate && patternTemplate.BudgetSummary && patternTemplate.BudgetSummary.Margin < 20 ?
    "#f44336" :
    "#4caf50"
}

export function getSchedulesByFilterTemplate(schedules: IScheduleDocument[], filterTemplate: IResourcePlannerScheduleFilter, employeeTypeData: IEmployeeTypeData[]): IScheduleDocument[] {
  return _.cloneDeep(schedules).map(schedule => {
    schedule.Schedules = schedule.Schedules.filter(pt => {
      let returnVal = true
      if (filterTemplate) {
        if (pt.AssignedUserId) {
          const assignEmployeeType = employeeTypeData.find(i => i.Id === pt.AssignedUserId)
          if (assignEmployeeType) {
            if (filterTemplate.HideCoreStaff && !assignEmployeeType.Agency && !assignEmployeeType.Float && !assignEmployeeType.PerDiem) {
              returnVal = false
            }
            if (filterTemplate.HideAgencyStaff && assignEmployeeType.Agency) {
              returnVal = false
            }
            if (filterTemplate.HideFloatStaff && assignEmployeeType.Float) {
              returnVal = false
            }
            if (filterTemplate.HidePerDiemStaff && assignEmployeeType.PerDiem) {
              returnVal = false
            }
          }
        }
        if (filterTemplate.ViewOnlyProductiveShifts && isNonProductive(pt)) {
          returnVal = false
        }
        if (filterTemplate.ViewOnlyFilledShifts && !pt.AssignedUserId) {
          returnVal = false
        }
        if (filterTemplate.ViewOnlyUnfilledShifts && pt.AssignedUserId) {
          returnVal = false
        }
        if (filterTemplate.ViewUnscheduledStaff) {

        }
        if (filterTemplate.BasePositionIds && filterTemplate.BasePositionIds.length > 0) {
          if (!pt.ShiftTemplates[0].Position || !filterTemplate.BasePositionIds.includes(pt.ShiftTemplates[0].Position.BasePositionId)) {
            returnVal = false
          }
        }
        if (filterTemplate.ClientIds && filterTemplate.ClientIds.length > 0) {
          if (!filterTemplate.ClientIds.includes(pt.ShiftTemplates[0].ClientId)) {
            returnVal = false
          }
        }
        if (filterTemplate.JobIds && filterTemplate.JobIds.length > 0) {
          if (!filterTemplate.JobIds.includes(pt.ShiftTemplates[0].JobId)) {
            returnVal = false
          }
        }
      }
      return returnVal
    })
    return schedule
  })
}

export function initScheduleBudgetDays(schedule: IScheduleDocument): IScheduleBudgetDay[] {
  let budgetDays: IScheduleBudgetDay[] = []

  if (schedule.Budget && schedule.Budget.Days && schedule.Budget.Days.length) {
    budgetDays = schedule.Budget.Days
  }

  const dayLength = getDayLengthFromStartAndEndTime(schedule.StartDate, schedule.EndDate)
  for (let i = 0; i < dayLength; i++) {
    const day = moment(timeHelper.keepConvertUTCToLocal(schedule.StartDate)).startOf("day").add(i, "days")
    if (!budgetDays.find(i => moment(timeHelper.keepConvertUTCToLocal(i.Date)).startOf("day").isSame(day))) {
      budgetDays.push({ Date: day.utc(true).toDate(), BudgetPositions: [] } as IScheduleBudgetDay)
    }
  }
  return budgetDays
}


export function isSingleBudgetVolumeUnit(budgetPositions: IScheduleBudgetDayPosition[]) {
  let returnVal = true
  if (budgetPositions && budgetPositions.length > 0) {
    const volumeUnitId = budgetPositions[0].Unit ? budgetPositions[0].Unit.UnitId : null
    budgetPositions.forEach(item => {
      if (volumeUnitId !== (item.Unit ? item.Unit.UnitId : null)) {
        returnVal = false
      }
    })
  }
  return returnVal
}

export function getSingleBudgetVolumeUnitValue(budgetPositions) {
  if (budgetPositions && budgetPositions.length > 0) {
    return budgetPositions[0].DemandUnits
  }

  return 0
}

export function unAssignEmployeeForSchedule(originalSchedule: IScheduleDocument, unAssignPatternTemplates: ISchedulePatternTemplate[]): IScheduleDocument {
  const schedule = _.cloneDeep(originalSchedule)
  const posted = schedule.PublishInfo && schedule.PublishInfo.Published

  unAssignPatternTemplates.forEach(patternTemplate => {
    const ptIndex = schedule.Schedules.findIndex(pt => {
      if (pt.Id === patternTemplate.Id) {
        if (posted) {
          pt.Deleted = true
          pt.ShiftTemplates.forEach(st => {
            st.Deleted = true
          })
        }
        else {
          pt.AssignedUserId = null
          pt.AssignedUserName = null
          pt.AssignedUserExternalId = null
          pt.AssignedUserProfileImageS3Url = null
          pt.AutoAssigned = false
          pt.AutoAssignAuditNotes = null
        }
      }
    })

    if (posted) {
      patternTemplate.Id = utils.guid()
      patternTemplate.AssignedUserId = null
      patternTemplate.AssignedUserName = null
      patternTemplate.AssignedUserExternalId = null
      patternTemplate.AssignedUserProfileImageS3Url = null
      patternTemplate.AutoAssigned = false
      patternTemplate.AutoAssignAuditNotes = null
      patternTemplate.ShiftTemplates.forEach(shift => {
        shift.UserId = null
        shift.Id = utils.guid()
      })

      schedule.Schedules.splice(ptIndex, 0, patternTemplate)
    }
  })
  return schedule
}

export function checkDuplicatedScheduleTemplateName(name: string, _hierarchyId: string, _jobId: string): boolean {
  const hierarchyId = _hierarchyId || null
  const jobId = _jobId || null
  const existingScheduleTemplates = AccountStore.getCurrentAccount().ScheduleData && AccountStore.getCurrentAccount().ScheduleData.ScheduleTemplates ?
    AccountStore.getCurrentAccount().ScheduleData.ScheduleTemplates :
    []
  return existingScheduleTemplates
    .filter(item => !item.Deleted && item.HierarchyId === hierarchyId && item.JobId === jobId)
    .find(item => item.Name === name.trim()) ? true : false
}

export function getCommonHierarchyJobData(schedules: (IScheduleDocument | IScheduleTemplate)[]) {
  let hierarchyId = schedules[0].HierarchyId
  let jobId = schedules[0].JobId
  schedules.forEach(i => {
    if (i.HierarchyId !== hierarchyId) {
      hierarchyId = AccountStore.DiffHierarchyLevel
    }
    if (i.JobId !== jobId) {
      jobId = AccountStore.DiffJobId
    }
  })
  return { hierarchyId, jobId }
}

export function getAllShiftIds(patternTemplates: ISchedulePatternTemplate[]): string[] {
  const shiftIds = []
  patternTemplates.forEach(i => {
    i.ShiftTemplates.forEach(j => {
      shiftIds.push(j.Id)
    })
  })
  return shiftIds
}